/// <reference path="../../types.d.ts" />
import { t } from "./i18n";

export function normalizeText(str: unknown): string {
  if (!str) return "";
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .trim();
}

export function normalizePhonetic(str: unknown): string {
  if (!str) return "";
  let s = String(str).toLowerCase();
  s = s.replace(/đ/g, "dj");
  s = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return s
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ş/g, "s")
    .replace(/ç/g, "c")
    .replace(/ž/g, "z")
    .replace(/š/g, "s")
    .replace(/č/g, "c")
    .replace(/ć/g, "c")
    .replace(/dz/g, "c")
    .replace(/dj/g, "c")
    .replace(/[^a-z0-9]/g, "")
    .trim();
}

export function scorePersonSearch(p: PersonRecord | null | undefined, q: string, qPhonetic: string): number {
  if (!p) return 0;
  let score = 0;
  const givenNorm = normalizeText(p.given || "");
  const surnameNorm = normalizeText(p.surname || "");
  const nameNorm = normalizeText(p.name || "");

  if (q) {
    if (givenNorm === q) score = 100;
    else if (nameNorm === q) score = 95;
    else if (givenNorm.startsWith(q)) score = 85;
    else if (surnameNorm.startsWith(q)) score = 75;
    else if (nameNorm.split(/\s+/).some((w: string) => w.startsWith(q))) score = 65;
    else if (givenNorm.includes(q)) score = 45;
    else if (surnameNorm.includes(q)) score = 35;
    else if (nameNorm.includes(q)) score = 25;
    else if (p._searchNorm && p._searchNorm.includes(q)) score = 10;
  }

  if (qPhonetic && p._phoneticNorm) {
    if (p._phoneticNorm.startsWith(qPhonetic)) {
      score = Math.max(score, 70);
    } else if (p._phoneticNorm.includes(qPhonetic)) {
      score = Math.max(score, 30);
    }
  }

  return score;
}

export interface PersonSearchOptions {
  excludeId?: string;
  limit?: number;
}

export function searchPersons(
  personList: PersonRecord[] | null | undefined,
  rawQuery: string,
  options?: PersonSearchOptions
): PersonRecord[] {
  if (!personList || !rawQuery) return [];
  const q = normalizeText(rawQuery.trim());
  const qPhonetic = normalizePhonetic(rawQuery.trim());
  if (!q && !qPhonetic) return [];

  const limit = options?.limit ?? 10;
  const excludeId = options?.excludeId;
  const scored: { person: PersonRecord; score: number }[] = [];

  for (const p of personList) {
    if (excludeId && p.id === excludeId) continue;
    const score = scorePersonSearch(p, q, qPhonetic);
    if (score > 0) {
      scored.push({ person: p, score });
    }
  }

  scored.sort((a, b) => b.score - a.score || a.person.name.localeCompare(b.person.name, "tr"));
  return scored.slice(0, limit).map((item) => item.person);
}

export function escapeHtml(str: unknown): string {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function highlightMatch(text: string, query: string): string {
  if (!text || !query) return escapeHtml(text || "");
  const normQuery = normalizeText(query);
  if (!normQuery) return escapeHtml(text);
  const normText = normalizeText(text);
  const idx = normText.indexOf(normQuery);
  if (idx === -1) return escapeHtml(text);
  const matchLen = normQuery.length;
  const before = escapeHtml(text.slice(0, idx));
  const match = escapeHtml(text.slice(idx, idx + matchLen));
  const after = escapeHtml(text.slice(idx + matchLen));
  return `${before}<mark class="search-highlight">${match}</mark>${after}`;
}

export function normalizePasscode(str: unknown): string {
  return normalizeText(str).replace(/[^a-z0-9]/g, "");
}

export function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const buf = new ArrayBuffer(bin.length);
  const arr = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

export function bytesToBase64(bytes: ArrayBuffer | Uint8Array): string {
  let bin = "";
  const arr = new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i++) bin += String.fromCharCode(arr[i]);
  return btoa(bin);
}

export function extractNumericYear(str: unknown): number | null {
  if (!str) return null;
  const m = String(str).match(/\b(1[456789]\d\d|20\d\d)\b/);
  return m ? parseInt(m[1], 10) : null;
}

export function formatLifeYears(p: PersonRecord): string {
  const b = p.birthYear;
  const d = p.deathYear;
  const bYr = extractNumericYear(b);
  const dYr = extractNumericYear(d);
  const curYear = new Date().getFullYear();

  if (b && d) {
    const age = bYr && dYr && dYr >= bYr ? ` | ${dYr - bYr}` : "";
    return `${b} – ${d}${age}`;
  }
  if (b && p.deceased) return `${b} – ?`;
  if (b) {
    const age = bYr && curYear >= bYr && curYear - bYr <= 125 ? ` | ${curYear - bYr}` : "";
    return `${b}${age}`;
  }
  if (d) return `? – ${d}`;
  if (p.deceased) return t("deceased");
  return t("living");
}

export function renderAvatarSilhouette(sex: string, isDeceased: boolean): string {
  const isFemale = sex === "F";
  const ghostClass = isDeceased ? "is-ghost" : "is-solid";
  const sexClass = isFemale ? "sex-F" : "sex-M";

  if (isFemale) {
    return `<svg class="avatar-svg ${sexClass} ${ghostClass}" viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true"><circle class="sil-head" cx="12" cy="5.2" r="3" /><path class="sil-body" d="M 9.8 8.4 C 9.4 8.4 9 8.7 9 9.1 L 6.8 16.8 C 6.6 17.3 7 17.8 7.7 17.8 H 10 V 21 C 10 21.4 10.4 21.8 10.9 21.8 C 11.4 21.8 11.7 21.4 11.7 21 V 17.8 H 12.3 V 21 C 12.3 21.4 12.6 21.8 13.1 21.8 C 13.6 21.8 14 21.4 14 21 V 17.8 H 16.3 C 17 17.8 17.4 17.3 17.2 16.8 L 15 9.1 C 15 8.7 14.6 8.4 14.2 8.4 Z" /></svg>`;
  } else {
    return `<svg class="avatar-svg ${sexClass} ${ghostClass}" viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true"><circle class="sil-head" cx="12" cy="5.2" r="3" /><path class="sil-body" d="M 8.5 9.6 C 8.5 8.8 9.2 8.2 10 8.2 H 14 C 14.8 8.2 15.5 8.8 15.5 9.6 V 14.5 H 14.2 V 21 C 14.2 21.4 13.8 21.8 13.3 21.8 C 12.8 21.8 12.4 21.4 12.4 21 V 16 H 11.6 V 21 C 11.6 21.4 11.2 21.8 10.7 21.8 C 10.2 21.8 9.8 21.4 9.8 21 V 14.5 H 8.5 Z" /></svg>`;
  }
}

export function downloadBlob(content: BlobPart, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function wrapSvgTextLines(text: unknown, maxCharsPerLine: number = 17): string[] {
  const clean = String(text || "").trim();
  if (clean.length <= maxCharsPerLine) return [clean];
  const words = clean.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const w of words) {
    if (!current) {
      current = w;
    } else if ((current + " " + w).length <= maxCharsPerLine) {
      current += " " + w;
    } else {
      lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  if (lines.length > 2) {
    return [lines[0], lines.slice(1).join(" ").slice(0, maxCharsPerLine + 2) + "…"];
  }
  return lines;
}
